import isEmpty from 'lodash/isEmpty.js';
import map from 'lodash/map.js';
import merge from 'lodash/merge.js';

import moment from '@/plugins/vue-moment.js';
import DiscussionReport from '@/store/modules/discussionreports/DiscussionReport.js';
import Collection from '@/store/quill/Collection.js';

export default merge(Collection('DiscussionReports', DiscussionReport), {
    state: {
        sort: {
            title: 'ASC',
            description: 'DESC',
        },
        filter: {},
    },

    getters: {},

    actions: {
        async fetchDiscussionReport({ dispatch }, courseId) {
            const enrollments = await dispatch('fetchAll', {
                filter: { key: 'course_id', value: courseId },
                endpoint: 'course-enrollments',
                raw: { per_page: 100 },
            }).then(resp => resp.data.data);

            const instructors = enrollments.filter(user => user.roles.some(role => role.name === 'Instructor'));

            const activities = await dispatch('fetchAll', {
                filters: [
                    { key: 'activity_type_id', value: 4 },
                    { key: 'course_id', value: courseId },
                ],
                endpoint: 'activities',
            }).then(resp => resp.data.data);

            const sectionIds = activities.filter(activity => activity.section_id).map(item => item.section_id);

            const sections = await dispatch('fetchAll', {
                filters: [{ key: 'id', value: sectionIds }],
                include: ['sectionSettings'],
                endpoint: 'sections',
            }).then(resp => resp.data.data);

            const activityIds = activities.map(activity => activity.id);

            const data = await Promise.all(
                instructors.map(instructor =>
                    dispatch('fetchAll', {
                        filter: { key: 'activity_id', value: activityIds },
                        raw: {
                            reply_count_for_user: instructor.user_id,
                            per_page: 500,
                        },
                        endpoint: 'discussion-threads',
                    }).then(resp => {
                        return {
                            ...resp.data,
                            data: resp.data.data.filter(item => item.user_id !== instructor.user_id),
                            instructor: instructor.user,
                            id: instructor.user_id,
                            courseId,
                        };
                    })
                )
            );

            const mergedData = await dispatch('fetchInstructorReplies', data);

            const sortedData = await dispatch('sortDiscussions', {
                instructors: mergedData,
                activities,
                sections,
            });

            sortedData.forEach(item => dispatch('add', item));
        },

        sortDiscussions(context, { instructors, activities, sections }) {
            const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

            return instructors.map(instructor => {
                let sorted = {};

                activities.forEach(activity => {
                    const sectionIndex = sections.findIndex(section => section.id === activity.section_id);

                    sorted[activity.id] = {
                        activity,
                        section: sectionIndex >= 0 ? sections[sectionIndex] : null,
                        activityId: activity.id,
                        data: [],
                    };
                });

                instructor.data.forEach(thread => {
                    sorted[thread.activity_id].data.push(thread);
                });

                sorted = map(sorted, item => {
                    const countKey = 'reply_count_for_user';
                    const replyCount = item.data.reduce((a, b) => a + (Number(b[countKey]) > 0 ? 1 : 0), 0);

                    item.data.forEach(thread => {
                        let responseDuration = null;

                        const replies = instructor.replies.filter(reply => reply.thread_id === thread.id);

                        replies.sort((a, b) => {
                            return moment(a.created_at).valueOf() - moment(b.created_at).valueOf();
                        });

                        if (!isEmpty(replies)) {
                            const postDate = moment.utc(thread.created_at).tz(timezone);
                            const responseDate = moment.utc(replies[0].created_at).tz(timezone);

                            responseDuration = Math.abs(postDate.diff(responseDate, 'days'));
                        }

                        merge(thread, { replies, responseDuration });
                    });

                    const responseDurationTotal = item.data.reduce((a, b) => a + b.responseDuration, 0);

                    let responseDuration;

                    if (responseDurationTotal === 0) {
                        responseDuration = null;
                    } else {
                        responseDuration =
                            Number(responseDurationTotal) /
                            Number(item.data.filter(item => item.responseDuration).length);
                    }

                    return {
                        ...item,
                        repliesTotal: replyCount,
                        responseDuration,
                    };
                });

                let totalAverageResponse;

                const averageResponseSum = sorted.reduce((a, b) => a + Number(b.responseDuration), 0);

                const activitiesWithResponses = sorted.filter(item => item.responseDuration);

                if (activitiesWithResponses.length === 0) {
                    totalAverageResponse = null;
                } else {
                    totalAverageResponse = averageResponseSum / activitiesWithResponses.length;
                }

                return { ...instructor, data: sorted, totalAverageResponse };
            });
        },

        fetchInstructorReplies({ dispatch }, instructors) {
            return Promise.all(
                instructors.map(instructor => {
                    const threadIds = instructor.data.map(thread => thread.id);

                    return dispatch('fetchAll', {
                        filters: [
                            { key: 'thread_id', value: threadIds },
                            { key: 'user_id', value: instructor.id },
                        ],
                        raw: { sort: 'created_at', per_page: 500 },
                        endpoint: 'discussion-replies',
                    }).then(resp => {
                        return {
                            ...instructor,
                            replies: resp.data.data,
                        };
                    });
                })
            );
        },

        async getNextPage(context, { page, id }) {
            const item = context.getters.get(id);

            const { data } = await context.dispatch('fetchAll', {
                filter: { key: 'course_id', value: item.courseId },
                raw: {
                    reply_count_for_user: id,
                    page,
                },
                include: ['activity'],
                endpoint: 'discussion-threads',
            });

            await context.commit(
                `DiscussionReports/${id}/EDIT`,
                {
                    data: data.data,
                    meta: data.meta,
                },
                { root: true }
            );
        },
    },

    mutations: {},
});
